import { calculateMargin } from '@/types/rateCard.types';
import type { RateCardEntry, RateCardSummary, RoleRate, UpdateRoleRateRequest, UpdatePersonRateRequest, AssignedPerson } from '@/types/rateCard.types';
import { MockOrgDefaultsService } from './mockOrgDefaults.service';

const STORAGE_KEYS = {
  PROJECT_RATES: 'projectRates',
  PERSON_RATES: 'personRates',
  RATE_HISTORY: 'rateHistory',
};

export interface ProjectAllocationFilter {
  allocatedRoles?: string[]; // Array of role names allocated to project
  allocatedPeople?: Array<{ personId: string; personName: string; role: string }>; // People allocated to project
}

export class RateCardStorageService {
  /**
   * Get rate card summary for a project
   */
  static getRateCardSummary(projectId: string, filter?: ProjectAllocationFilter): RateCardSummary {
    const roleRates = this.getAllRoleRates(projectId, filter);

    if (roleRates.length === 0) {
      return {
        averageHourlyBillRate: 0,
        averageHourlyCostRate: 0,
        averageDailyBillRate: 0,
        averageDailyCostRate: 0,
        averageMargin: 0,
        roleCount: 0,
        lastUpdated: new Date().toISOString(),
        lastUpdatedBy: 'System',
      };
    }

    const totalHourlyBill = roleRates.reduce((sum, r) => sum + r.hourlyBillRate, 0);
    const totalHourlyCost = roleRates.reduce((sum, r) => sum + r.hourlyCostRate, 0);
    const totalDailyBill = roleRates.reduce((sum, r) => sum + r.dailyBillRate, 0);
    const totalDailyCost = roleRates.reduce((sum, r) => sum + r.dailyCostRate, 0);

    const avgHourlyBill = totalHourlyBill / roleRates.length;
    const avgHourlyCost = totalHourlyCost / roleRates.length;
    const avgDailyBill = totalDailyBill / roleRates.length;
    const avgDailyCost = totalDailyCost / roleRates.length;

    return {
      averageHourlyBillRate: Math.round(avgHourlyBill * 100) / 100,
      averageHourlyCostRate: Math.round(avgHourlyCost * 100) / 100,
      averageDailyBillRate: Math.round(avgDailyBill * 100) / 100,
      averageDailyCostRate: Math.round(avgDailyCost * 100) / 100,
      averageMargin: calculateMargin(avgHourlyBill, avgHourlyCost),
      roleCount: roleRates.length,
      lastUpdated: new Date().toISOString(),
      lastUpdatedBy: 'Current User',
    };
  }

  /**
   * Get all role rates for a project (with rate hierarchy resolution)
   * Optionally filtered by allocated roles and people
   */
  static getAllRoleRates(projectId: string, filter?: ProjectAllocationFilter): RoleRate[] {
    const orgDefaults = MockOrgDefaultsService.getOrgDefaultRates();
    const projectOverrides = this.getProjectOverrides(projectId);

    // Filter org defaults by allocated roles if filter provided
    const filteredOrgDefaults = filter?.allocatedRoles 
      ? orgDefaults.filter(orgRate => filter.allocatedRoles!.includes(orgRate.roleName))
      : orgDefaults;

    return filteredOrgDefaults.map(orgRate => {
      // Check if there's a project override for this role
      const override = projectOverrides.find(o => o.roleId === orgRate.roleId);

      // Get assigned people for this role
      const assignedPeople = this.getAssignedPeopleForRole(projectId, orgRate.roleId, filter);

      if (override) {
        // Use project override
        return {
          roleId: orgRate.roleId,
          roleName: orgRate.roleName,
          hourlyCostRate: override.hourlyCostRate,
          hourlyBillRate: override.hourlyBillRate,
          dailyCostRate: override.dailyCostRate,
          dailyBillRate: override.dailyBillRate,
          margin: calculateMargin(override.hourlyBillRate, override.hourlyCostRate),
          effectiveFrom: override.effectiveFrom,
          source: 'Project Override' as const,
          assignedPeople,
        };
      } else {
        // Use org default
        return {
          roleId: orgRate.roleId,
          roleName: orgRate.roleName,
          hourlyCostRate: orgRate.hourlyCostRate,
          hourlyBillRate: orgRate.hourlyBillRate,
          dailyCostRate: orgRate.dailyCostRate,
          dailyBillRate: orgRate.dailyBillRate,
          margin: calculateMargin(orgRate.hourlyBillRate, orgRate.hourlyCostRate),
          effectiveFrom: new Date().toISOString(),
          source: 'Org Default' as const,
          assignedPeople,
        };
      }
    });
  }

  /**
   * Get assigned people for a role with their rates
   * Optionally filtered by allocated people
   */
  private static getAssignedPeopleForRole(projectId: string, roleId: string, filter?: ProjectAllocationFilter): AssignedPerson[] {
    const orgPeople = MockOrgDefaultsService.getPeopleByRole(roleId);
    const personOverrides = this.getPersonOverrides(projectId);
    const orgDefault = MockOrgDefaultsService.getOrgDefaultRateByRole(roleId);

    if (!orgDefault) return [];

    // Filter people by allocated people if filter provided
    const filteredPeople = filter?.allocatedPeople
      ? orgPeople.filter(person => 
          filter.allocatedPeople!.some(allocated => 
            allocated.personId === person.personId && allocated.role === orgDefault.roleName
          )
        )
      : orgPeople;

    return filteredPeople.map(person => {
      // Check if there's a person override
      const override = personOverrides.find(
        o => o.personId === person.personId && o.roleId === roleId
      );

      if (override) {
        return {
          personId: person.personId,
          personName: person.personName,
          hourlyCostRate: override.hourlyCostRate,
          hourlyBillRate: override.hourlyBillRate,
          dailyCostRate: override.dailyCostRate,
          dailyBillRate: override.dailyBillRate,
          margin: calculateMargin(override.hourlyBillRate, override.hourlyCostRate),
          effectiveFrom: override.effectiveFrom,
          source: 'Person Override' as const,
        };
      } else {
        return {
          personId: person.personId,
          personName: person.personName,
          hourlyCostRate: orgDefault.hourlyCostRate,
          hourlyBillRate: orgDefault.hourlyBillRate,
          dailyCostRate: orgDefault.dailyCostRate,
          dailyBillRate: orgDefault.dailyBillRate,
          margin: calculateMargin(orgDefault.hourlyBillRate, orgDefault.hourlyCostRate),
          effectiveFrom: new Date().toISOString(),
          source: 'Org Default' as const,
        };
      }
    });
  }

  /**
   * Get project-level rate overrides from localStorage
   */
  private static getProjectOverrides(projectId: string): UpdateRoleRateRequest[] {
    const stored = localStorage.getItem(`${STORAGE_KEYS.PROJECT_RATES}_${projectId}`);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Get person-level rate overrides from localStorage
   */
  private static getPersonOverrides(projectId: string): (UpdatePersonRateRequest & { roleId: string })[] {
    const stored = localStorage.getItem(`${STORAGE_KEYS.PERSON_RATES}_${projectId}`);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Update a role rate (creates project override)
   */
  static updateRoleRate(projectId: string, request: UpdateRoleRateRequest): void {
    const overrides = this.getProjectOverrides(projectId);
    
    // Remove existing override for this role
    const filtered = overrides.filter(o => o.roleId !== request.roleId);
    
    // Add new override
    filtered.push(request);
    
    // Save to localStorage
    const storageKey = `${STORAGE_KEYS.PROJECT_RATES}_${projectId}`;
    localStorage.setItem(storageKey, JSON.stringify(filtered));
    console.log('[RateCardStorageService] Saved role rate to localStorage:', storageKey, request);
    
    // Dispatch custom event to notify other components
    console.log('[RateCardStorageService] Dispatching rateCardUpdated event for projectId:', projectId);
    window.dispatchEvent(new CustomEvent('rateCardUpdated', { detail: { projectId } }));
    
    // Add to history
    this.addToHistory(projectId, {
      id: `history-${Date.now()}`,
      projectId,
      roleId: request.roleId,
      roleName: MockOrgDefaultsService.getOrgDefaultRateByRole(request.roleId)?.roleName || 'Unknown',
      hourlyAmount: request.hourlyBillRate,
      dailyAmount: request.dailyBillRate,
      rateType: 'Bill',
      effectiveFrom: request.effectiveFrom,
      source: 'Project Override',
      createdBy: 'Current User',
      createdDate: new Date().toISOString(),
      notes: request.notes,
    });
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('rateCardUpdated', { detail: { projectId } }));
  }

  /**
   * Update a person rate (creates person override)
   */
  static updatePersonRate(projectId: string, request: UpdatePersonRateRequest): void {
    const overrides = this.getPersonOverrides(projectId);
    
    // Remove existing override for this person
    const filtered = overrides.filter(
      o => !(o.personId === request.personId && o.roleId === request.roleId)
    );
    
    // Add new override
    filtered.push(request);
    
    // Save to localStorage
    const storageKey = `${STORAGE_KEYS.PERSON_RATES}_${projectId}`;
    localStorage.setItem(storageKey, JSON.stringify(filtered));
    console.log('[RateCardStorageService] Saved person rate to localStorage:', storageKey, request);
    
    // Dispatch custom event to notify other components
    console.log('[RateCardStorageService] Dispatching rateCardUpdated event for projectId:', projectId);
    window.dispatchEvent(new CustomEvent('rateCardUpdated', { detail: { projectId } }));
    
    // Add to history
    const person = MockOrgDefaultsService.getPersonById(request.personId);
    this.addToHistory(projectId, {
      id: `history-${Date.now()}`,
      projectId,
      roleId: request.roleId,
      roleName: MockOrgDefaultsService.getOrgDefaultRateByRole(request.roleId)?.roleName || 'Unknown',
      personId: request.personId,
      personName: person?.personName || 'Unknown',
      hourlyAmount: request.hourlyBillRate,
      dailyAmount: request.dailyBillRate,
      rateType: 'Bill',
      effectiveFrom: request.effectiveFrom,
      source: 'Person Override',
      createdBy: 'Current User',
      createdDate: new Date().toISOString(),
      notes: request.notes,
    });
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('rateCardUpdated', { detail: { projectId } }));
  }

  /**
   * Get effective rate for a specific person
   * Applies hierarchy: person override > project override > org default
   */
  static getEffectivePersonRate(
    projectId: string,
    personId: string,
    roleId: string
  ): {
    hourlyCostRate: number;
    hourlyBillRate: number;
    dailyCostRate: number;
    dailyBillRate: number;
    source: string;
    margin: number;
  } {
    // 1. Check person override (highest priority)
    const personOverrides = this.getPersonOverrides(projectId);
    const personOverride = personOverrides.find(
      o => o.personId === personId && o.roleId === roleId
    );
    if (personOverride) {
      return {
        hourlyCostRate: personOverride.hourlyCostRate,
        hourlyBillRate: personOverride.hourlyBillRate,
        dailyCostRate: personOverride.dailyCostRate,
        dailyBillRate: personOverride.dailyBillRate,
        source: 'Person Override',
        margin: calculateMargin(personOverride.hourlyBillRate, personOverride.hourlyCostRate),
      };
    }

    // 2. Check project override
    const projectOverrides = this.getProjectOverrides(projectId);
    const projectOverride = projectOverrides.find(o => o.roleId === roleId);
    if (projectOverride) {
      return {
        hourlyCostRate: projectOverride.hourlyCostRate,
        hourlyBillRate: projectOverride.hourlyBillRate,
        dailyCostRate: projectOverride.dailyCostRate,
        dailyBillRate: projectOverride.dailyBillRate,
        source: 'Project Override',
        margin: calculateMargin(projectOverride.hourlyBillRate, projectOverride.hourlyCostRate),
      };
    }

    // 3. Fall back to org default (lowest priority)
    const orgDefault = MockOrgDefaultsService.getOrgDefaultRateByRole(roleId);
    if (orgDefault) {
      return {
        hourlyCostRate: orgDefault.hourlyCostRate,
        hourlyBillRate: orgDefault.hourlyBillRate,
        dailyCostRate: orgDefault.dailyCostRate,
        dailyBillRate: orgDefault.dailyBillRate,
        source: 'Org Default',
        margin: calculateMargin(orgDefault.hourlyBillRate, orgDefault.hourlyCostRate),
      };
    }

    // 4. No rate found - return zeros
    return {
      hourlyCostRate: 0,
      hourlyBillRate: 0,
      dailyCostRate: 0,
      dailyBillRate: 0,
      source: 'Not Found',
      margin: 0,
    };
  }

  /**
   * Add entry to rate history
   */
  private static addToHistory(projectId: string, entry: RateCardEntry): void {
    const history = this.getRateHistory(projectId);
    history.unshift(entry); // Add to beginning (most recent first)
    localStorage.setItem(`${STORAGE_KEYS.RATE_HISTORY}_${projectId}`, JSON.stringify(history));
  }

  /**
   * Get rate change history for a project
   */
  static getRateHistory(projectId: string): RateCardEntry[] {
    const stored = localStorage.getItem(`${STORAGE_KEYS.RATE_HISTORY}_${projectId}`);
    return stored ? JSON.parse(stored) : [];
  }

  /**
   * Clear all project rates (for testing)
   */
  static clearProjectRates(projectId: string): void {
    localStorage.removeItem(`${STORAGE_KEYS.PROJECT_RATES}_${projectId}`);
    localStorage.removeItem(`${STORAGE_KEYS.PERSON_RATES}_${projectId}`);
    localStorage.removeItem(`${STORAGE_KEYS.RATE_HISTORY}_${projectId}`);
  }
}
